import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ArrowLeft, Edit, Trash2, CircleDollarSign, Search } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

function CentroCustoList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/centros-custo`);
      setItems(response.data);
    } catch (error) {
      toast.error("Erro ao carregar centros de custo");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/centros-custo/${id}`);
      toast.success("Centro de custo excluído");
      fetchItems();
    } catch (error) {
      toast.error("Erro ao excluir");
    }
  };

  const filteredItems = items.filter(item =>
    item.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.codigo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTipoBadge = (tipo) => {
    const tipos = {
      despesa: { label: "Despesa", class: "bg-red-100 text-red-700" },
      receita: { label: "Receita", class: "bg-green-100 text-green-700" },
      ambos: { label: "Ambos", class: "bg-blue-100 text-blue-700" }
    };
    return tipos[tipo] || tipos.despesa;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="centro-custo-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Centro de Custo</h1>
        <Link to="/financeiro/centro-custo/new">
          <Button className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="new-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Centro
          </Button>
        </Link>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Buscar por código ou nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map((item) => (
          <Card key={item.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="font-mono">{item.codigo}</Badge>
                    <Badge className={getTipoBadge(item.tipo).class}>
                      {getTipoBadge(item.tipo).label}
                    </Badge>
                    {!item.ativo && (
                      <Badge variant="secondary">Inativo</Badge>
                    )}
                  </div>
                  <h3 className="font-semibold text-luvcorp-navy">{item.nome}</h3>
                  {item.descricao && (
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.descricao}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t">
                <Link to={`/financeiro/centro-custo/${item.id}/edit`} className="flex-1">
                  <Button variant="outline" size="sm" className="w-full">
                    <Edit className="h-4 w-4 mr-2" />
                    Editar
                  </Button>
                </Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Excluir centro de custo?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Tem certeza que deseja excluir "{item.nome}"?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleDelete(item.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredItems.length === 0 && (
          <Card className="border-0 shadow-sm col-span-full">
            <CardContent className="p-12 text-center">
              <CircleDollarSign className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhum centro de custo encontrado</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function CentroCustoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    codigo: "",
    nome: "",
    descricao: "",
    tipo: "despesa",
    ativo: true,
    parent_id: null
  });

  const isEdit = !!id;

  useEffect(() => {
    if (isEdit) {
      fetchItem();
    }
  }, [id]);

  const fetchItem = async () => {
    try {
      const response = await axios.get(`${API}/centros-custo/${id}`);
      setFormData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
      navigate("/financeiro/centro-custo");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await axios.put(`${API}/centros-custo/${id}`, formData);
        toast.success("Centro de custo atualizado!");
      } else {
        await axios.post(`${API}/centros-custo`, formData);
        toast.success("Centro de custo criado!");
      }
      navigate("/financeiro/centro-custo");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao salvar"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="centro-custo-form">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/financeiro/centro-custo">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">
          {isEdit ? "Editar Centro de Custo" : "Novo Centro de Custo"}
        </h1>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="codigo">Código *</Label>
                <Input
                  id="codigo"
                  value={formData.codigo}
                  onChange={(e) => setFormData({...formData, codigo: e.target.value.toUpperCase()})}
                  placeholder="Ex: CC001"
                  required
                  className="font-mono"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo *</Label>
                <NativeSelect
                  value={formData.tipo}
                  onValueChange={(value) => setFormData({...formData, tipo: value})}
                  placeholder="Selecione o tipo"
                  options={[
                    { value: "despesa", label: "Despesa" },
                    { value: "receita", label: "Receita" },
                    { value: "ambos", label: "Ambos" }
                  ]}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nome">Nome *</Label>
              <Input
                id="nome"
                value={formData.nome}
                onChange={(e) => setFormData({...formData, nome: e.target.value})}
                placeholder="Nome do centro de custo"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Descrição do centro de custo"
                rows={3}
              />
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label htmlFor="ativo" className="font-medium">Status Ativo</Label>
                <p className="text-sm text-slate-500">Centro de custo disponível para uso</p>
              </div>
              <Switch
                id="ativo"
                checked={formData.ativo}
                onCheckedChange={(checked) => setFormData({...formData, ativo: checked})}
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white h-11"
              disabled={loading}
            >
              {loading ? "Salvando..." : (isEdit ? "Atualizar" : "Criar Centro de Custo")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function CentroCustoPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<CentroCustoList />} />
        <Route path="new" element={<CentroCustoForm />} />
        <Route path=":id/edit" element={<CentroCustoForm />} />
      </Routes>
    </MainLayout>
  );
}
